import * as React from 'react';
import { Drawer } from '../.././ui-native/Drawer';
import { Events } from '../../Events';
import { ReactUtils } from '../../ui-react/ReactUtils';
import { FormGroup } from '../.././ui-react/UtilComponents';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import './PageStateEditor.scss';
import { cs } from '@shared/utils/utils-classname';
import { Notify } from '@shared/ui-native/Notify';
import { Button } from '@shared/ui-react/Button';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';

export const PageStateEditor = {
    open(view: IPageState, currentState: IPageState, treeData: ITreeData, doSave?: (state: Partial<IPageState>) => Promise<any>) {
        if (!kuura.hasPrivilege('PROJECT_W')) return;

        const drawerElement = Drawer.open();
        Events.trigger('drawer-open.editor');

        ReactUtils.safeUnmountComponentAtNode(drawerElement);
        ReactUtils.safeRender(<SavedViewEditorPanel view={view} currentState={currentState} treeData={treeData} doSave={doSave} />, drawerElement);
    },

    close() {
        Drawer.close();
        Events.trigger('drawer-close.editor');
    },
};

@observer
class SavedViewEditorPanel extends React.Component<{
    view: IPageState;
    currentState?: IPageState;
    treeData: ITreeData;
    doSave?: (state: Partial<IPageState>) => Promise<any>;
}> {
    @observable name = this.props.view.name;
    @observable description = this.props.view.description ?? '';
    @observable folderId = this.props.view.folderId;
    @observable visibility = this.props.view.visibility;
    @observable overwriteData = false;

    @observable isLegacy = this.props.view.page === 'MAP3D_LEGACY';
    @observable isSaving = false;

    nameEl: HTMLInputElement;

    constructor(props: { view: IPageState; treeData: ITreeData }) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        setTimeout(() => {
            this.nameEl.focus();
        }, 100);
    }

    render() {
        const { view, treeData } = this.props;
        const isSameAuthor = view.user?.id === kuura.getUserId();

        return (
            <>
                <header
                    style={{
                        backgroundImage: 'linear-gradient(0, white 0, transparent), url("' + view.thumbnail + '")',
                        backgroundSize: 'cover',
                        height: 150,
                    }}
                ></header>

                <main id='saved-view-editor' className={cs({ 'overlay-loading': this.isSaving })}>
                    <form
                        autoComplete='off'
                        onSubmit={e => {
                            e.preventDefault();
                            this.handleSave();
                        }}
                    >
                        <FormGroup title={__('general.name')} checkable={false}>
                            <input type='text' ref={el => (this.nameEl = el)} maxLength={128} value={this.name} onChange={e => (this.name = e.currentTarget.value)} />
                            {this.name.length === 128 && (
                                <div className='ik-input-limit'>
                                    <span>{__('general.validation.character_limit_reached')}</span> <span>{this.name.length}/128</span>
                                </div>
                            )}
                            {this.isEdit && this.name.length === 0 && <div>{__('general.validation.cannot_be_empty')}</div>}
                        </FormGroup>

                        <FormGroup title={kuura.getMessage('general.description')}>
                            <textarea name='description' defaultValue={this.description} maxLength={255} onChange={e => (this.description = e.currentTarget.value)}></textarea>
                            {this.description.length === 255 && (
                                <div className='ik-input-limit'>
                                    <span>{__('general.validation.maximum_n_field_length', 255)}</span> <span>{this.description.length}/255</span>
                                </div>
                            )}
                            {this.description.length !== 255 && <span className='ik-input-limit-counter'>{this.description.length}/255</span>}
                        </FormGroup>

                        {!this.isLegacy && (
                            <>
                                <FormGroup title={__('general.folder')} checkable={false}>
                                    <FiletreeSelectReact
                                        formFieldName='folderId'
                                        selectable='folder'
                                        treeData={treeData}
                                        selectedId={this.folderId}
                                        onChange={val => (this.folderId = val ? parseInt(val) : null)}
                                        storageKey={'PageStateEditor_folderSelectTree'}
                                    />
                                </FormGroup>

                                <div className='form-group'>
                                    <label>{__('general.visibility')}</label>
                                    <div style={{ display: 'flex' }} className='btn-group btn-group-block ik-button-secondary--subtle'>
                                        <Button icon='iki iki-show' onClick={() => (this.visibility = 'PUBLIC')} className={cs({ active: this.visibility == 'PUBLIC' })}>
                                            {__('general.visibility_public')}
                                        </Button>

                                        <Button icon='iki iki-hide' onClick={() => (this.visibility = 'PRIVATE')} className={cs({ active: this.visibility == 'PRIVATE' })}>
                                            {__('general.visibility_private')}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}

                        {this.isEdit && isSameAuthor && (
                            <div className='form-group' style={{ marginTop: 10 }}>
                                <label style={{ textTransform: 'initial' }}>
                                    <input type='checkbox' checked={this.overwriteData} onChange={e => (this.overwriteData = e.currentTarget.checked)} />{' '}
                                    {__('js_saved_views.update_with_current_map_state')}
                                </label>
                            </div>
                        )}
                    </form>
                </main>

                <footer className='flex-spread'>
                    <button onClick={PageStateEditor.close}>{__('general.close')}</button>

                    <Button className='ik-button-primary' onClick={this.handleSave} disabled={!this.isFormValid()} loading={this.isSaving}>
                        {__(this.isEdit ? 'general.save' : 'general.create')}
                    </Button>
                </footer>
            </>
        );
    }

    isFormValid = () => {
        return (this.isEdit ? this.name.length > 0 : true) && this.description.length <= 255;
    };

    handleSave = () => {
        this.isSaving = true;
        let dataPatch = {};

        if (this.isEdit) {
            if (this.overwriteData && this.props.currentState) {
                dataPatch = {
                    data: this.props.currentState.data,
                    dataVersion: this.props.currentState.dataVersion,
                    thumbnail: this.props.currentState.thumbnail,
                };
            } else {
                delete this.props.view.data;
                delete this.props.view.dataVersion;
                delete this.props.view.thumbnail;
            }
        }

        this.props
            .doSave({
                ...this.props.view,
                ...dataPatch,
                name: this.name,
                description: this.description,
                folderId: this.folderId,
                visibility: this.visibility,
            })
            .then(() => {
                PageStateEditor.close();
            })
            .catch(() => {
                Notify.error();
            })
            .finally(() => {
                this.isSaving = false;
            });
    };

    get isEdit() {
        return !!this.props.view.uuid;
    }
}
